import { AccessTime, CalendarMonthOutlined } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  InputAdornment,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
} from '@mui/material';
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { lang } from 'src/utils';

export interface ITimeMachineParams {
  date: string;
  time: string;
  channel: string;
}

interface ITimeMachineStepperProps {
  timeMachineParams: ITimeMachineParams;
  onTimeMachineParamsChange: (key: string, value: string) => void;
  resetTimeMachineParams: () => void;
  channelOptions: string[];
  isChannelOptionsLoading: boolean;
  hasLocationVersion: boolean;
}

export const TimeMachineStepper = (props: ITimeMachineStepperProps) => {
  const {
    timeMachineParams,
    onTimeMachineParamsChange,
    resetTimeMachineParams,
    channelOptions,
    isChannelOptionsLoading,
    hasLocationVersion,
  } = props;
  const [activeStep, setActiveStep] = useState(0);

  const isStepCompleted = (timeMachineParams: ITimeMachineParams) => {
    return (
      timeMachineParams.date !== null ||
      timeMachineParams.time !== null ||
      timeMachineParams.channel !== null
    );
  };

  const onDateChange = (value: Dayjs | null) => {
    onTimeMachineParamsChange('date', value ? value.format('YYYY-MM-DD') : '');
  };

  const onTimeChange = (value: Dayjs | null) => {
    onTimeMachineParamsChange('time', value ? value.format('HH:mm') : '');
  };

  const onChannelChange = (value: string | null) => {
    onTimeMachineParamsChange('channel', value ?? '');
  };

  useEffect(() => {
    if (timeMachineParams.date && timeMachineParams.time) {
      setActiveStep(2);
    } else if (timeMachineParams.date) {
      setActiveStep(1);
    } else {
      setActiveStep(0);
    }
  }, [timeMachineParams]);

  return (
    <Box maxWidth="272px">
      <Stepper orientation="vertical" connector={null} activeStep={activeStep}>
        <Step expanded completed={!!timeMachineParams['date']}>
          <StepLabel>{lang.date}</StepLabel>
          <StepContent>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                value={
                  timeMachineParams.date ? dayjs(timeMachineParams.date) : null
                }
                autoFocus={false}
                onAccept={onDateChange}
                orientation="landscape"
                slotProps={{
                  textField: {
                    size: 'small',
                    placeholder: 'Select a date',
                    fullWidth: true,
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarMonthOutlined />
                        </InputAdornment>
                      ),
                    },
                  },
                  actionBar: {
                    actions: ['clear', 'accept'],
                  },
                }}
                onOpen={() => {
                  if (!timeMachineParams.date) onDateChange(dayjs());
                }}
              />
            </LocalizationProvider>
          </StepContent>
        </Step>

        <Step expanded completed={!!timeMachineParams['time']}>
          <StepLabel>{lang.time}</StepLabel>
          <StepContent>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileTimePicker
                value={
                  timeMachineParams.time
                    ? dayjs(timeMachineParams.time, 'HH:mm')
                    : null
                }
                autoFocus={false}
                orientation="landscape"
                onAccept={onTimeChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    placeholder: !!timeMachineParams.date
                      ? 'Select a time'
                      : 'None',
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <AccessTime />
                        </InputAdornment>
                      ),
                    },
                  },
                  actionBar: {
                    actions: ['clear', 'accept'],
                  },
                }}
                onOpen={() => {
                  if (!timeMachineParams.time)
                    onTimeChange(dayjs('00:00', 'HH:mm'));
                }}
                disabled={!timeMachineParams.date}
              />
            </LocalizationProvider>
          </StepContent>
        </Step>

        <Step expanded completed={!!timeMachineParams['channel']}>
          <StepLabel>{lang.channel}</StepLabel>
          <StepContent>
            <Autocomplete
              value={timeMachineParams.channel}
              options={channelOptions}
              loading={isChannelOptionsLoading}
              onChange={(_, value) => {
                onChannelChange(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': 'Choose channel',
                  }}
                  size="small"
                  placeholder={
                    !!timeMachineParams.date && !!timeMachineParams.time
                      ? 'Select a channel'
                      : 'None'
                  }
                />
              )}
              disabled={
                !timeMachineParams.date ||
                !timeMachineParams.time ||
                !hasLocationVersion
              }
            />
          </StepContent>
        </Step>
      </Stepper>

      {isStepCompleted(timeMachineParams) ? (
        <Grid container paddingTop={2} flexDirection="row-reverse">
          <Button
            variant="text"
            onClick={() => {
              resetTimeMachineParams();
            }}>
            {lang.reset}
          </Button>
        </Grid>
      ) : null}
    </Box>
  );
};
